import { useEffect, useRef, type KeyboardEvent } from "react";
import type { GuestNoun } from "@vertu/shared";
import { nounTitle } from "../../state/booking-questions-form";
import { riderChipLabel, riderChipState } from "../../state/checkout-steps";

/**
 * The rider chips of one line: "1 2 3 4" on a phone, "Rider 1 … Rider 4" as
 * tabs on a wider screen. Each shows a tick once that rider is done (saved,
 * on the full page; complete, in the drawer's pager), a dot for the rider on
 * screen and a ring for one still to do, and says so in words to a screen
 * reader ("Rider 2, saved"). A tablist: the arrow keys,
 * Home and End move between riders; the card on screen is the tabpanel.
 */
export function RiderTabs({
  label,
  noun,
  riders,
  current,
  compact,
  idPrefix,
  panelId,
  onSelect,
  done = "complete",
}: {
  /** The tablist's name: "Riders on To the Top". */
  label: string;
  noun: GuestNoun;
  /** This line's riders: their index among every rider card of the checkout. */
  riders: readonly { index: number; number: number; complete: boolean }[];
  /** The index of the rider on screen (it may be on another line). */
  current: number;
  compact: boolean;
  idPrefix: string;
  panelId: string;
  onSelect: (index: number) => void;
  /** What a tick means: "complete" (the drawer's pager) or "saved" (the full page). */
  done?: "complete" | "saved";
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const listRef = useRef<HTMLDivElement | null>(null);
  const here = riders.findIndex((r) => r.index === current);
  // The arrow keys ask for another rider; focus follows once the rider on
  // screen has actually changed (the full page saves the one it leaves
  // first, and a key pressed while that save is on its way is ignored), so
  // the focused tab and the card shown never disagree.
  useEffect(() => {
    const list = listRef.current;
    const active = document.activeElement;
    if (!list || !(active instanceof HTMLElement) || !list.contains(active) || here < 0) return;
    if (refs.current[here] !== active) refs.current[here]?.focus();
  }, [here]);
  function onKey(e: KeyboardEvent<HTMLButtonElement>, at: number) {
    const last = riders.length - 1;
    const to =
      e.key === "ArrowRight" || e.key === "ArrowDown"
        ? at === last ? 0 : at + 1
        : e.key === "ArrowLeft" || e.key === "ArrowUp"
          ? at === 0 ? last : at - 1
          : e.key === "Home"
            ? 0
            : e.key === "End"
              ? last
              : null;
    if (to === null) return;
    e.preventDefault();
    onSelect(riders[to]!.index);
  }
  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={label}
      className={"vt-rider-tabs" + (compact ? " vt-rider-tabs-compact" : "")}
    >
      {riders.map((rider, at) => {
        const selected = rider.index === current;
        const state = riderChipState(rider.complete, selected);
        return (
          <button
            key={rider.index}
            ref={(el) => {
              refs.current[at] = el;
            }}
            type="button"
            role="tab"
            id={`${idPrefix}-tab-${rider.index}`}
            className="vt-rider-tab"
            data-state={state}
            aria-selected={selected}
            aria-controls={selected ? panelId : undefined}
            aria-label={riderChipLabel(noun, rider.number, state, done)}
            // One tab stop per line: the rider on screen, else the first.
            tabIndex={selected || (here < 0 && at === 0) ? 0 : -1}
            onClick={() => onSelect(rider.index)}
            onKeyDown={(e) => onKey(e, at)}
          >
            <span className="vt-rider-tab-text">{compact ? rider.number : `${nounTitle(noun)} ${rider.number}`}</span>
            <span className="vt-rider-tab-mark" aria-hidden="true" />
          </button>
        );
      })}
    </div>
  );
}
